import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { usePermission } from '../../hooks/usePermission';
import { useOnboardingState } from '../../hooks/useOnboardingState';
import { entriesSince } from '../../hooks/useOnboardingState';
import { ChangelogEntry } from '../../data/changelog';
import WhatsNewDialog from './WhatsNewDialog';
import ReleaseHistoryDialog from './ReleaseHistoryDialog';
import { OnboardingContext, OnboardingContextValue } from './OnboardingContext';
import { startTour as startGuidedTour } from './GuidedTour';

interface OnboardingOrchestratorProps {
  children?: React.ReactNode;
}

/**
 * Top-level onboarding coordinator. Rendered once inside Layout (child of
 * ProtectedRoute) so it runs after auth. It:
 *  - decides whether the "What's New" popup should auto-open for returning users
 *    after a version bump (first-run users get NO popup — the tour handles them);
 *  - owns Release History open state and exposes imperative handlers via
 *    OnboardingContext so Header (user menu) and Layout (footer chip) can trigger
 *    them;
 *  - provides `startTour` as a no-op placeholder (see the SEAM comment below).
 */
const OnboardingOrchestrator: React.FC<OnboardingOrchestratorProps> = ({ children }) => {
  const { user } = useAuth();
  const {
    lastSeenVersion,
    isFirstRun,
    hasVersionBump,
    tourCompleted,
    markVersionSeen,
    markTourCompleted,
  } = useOnboardingState();

  // Tour visibility is derived from the RBAC matrix (N1 sweep) so the tour highlights
  // exactly what this user can see and never diverges from their actual capabilities.
  const { isAdmin, role: resolvedRole } = usePermissionContext();
  const role = resolvedRole || (isAdmin ? 'admin' : 'warehouse-manager');
  const canViewImageGen = usePermission('image-generation').can('view');
  const canViewInventory = usePermission('view-inventory').can('view');
  const canViewBundleSkus = usePermission('bundle-skus').can('view');
  const canViewCarding = usePermission('carding').can('view');
  const isPhotographyOnly =
    canViewImageGen && !canViewInventory && !canViewBundleSkus && !canViewCarding;

  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewEntries, setWhatsNewEntries] = useState<ChangelogEntry[]>([]);
  const [releaseHistoryOpen, setReleaseHistoryOpen] = useState(false);

  // Run the first-run / version-bump decision exactly once after the user is
  // present. A ref guards against re-running when auth state objects change.
  const decidedRef = useRef(false);

  // Hold the latest startTour so the decision effect can fire it without taking
  // it as a dependency (the callback is redefined when role/flags change, but we
  // only want to auto-start once per app load).
  const startTourRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!user || decidedRef.current) return;
    decidedRef.current = true;

    if (isFirstRun) {
      // Brand-new user: do NOT show What's New — the tour owns first-run
      // onboarding. Silently record the current version so the popup never
      // appears retroactively for this install, then auto-start the tour when it
      // has not been completed yet.
      markVersionSeen();
      if (!tourCompleted) {
        try {
          startTourRef.current();
        } catch (err) {
          console.warn('Auto-start tour failed:', err);
        }
      }
      return;
    }

    if (hasVersionBump) {
      const entries = entriesSince(lastSeenVersion);
      if (entries.length > 0) {
        setWhatsNewEntries(entries);
        setWhatsNewOpen(true);
      } else {
        // Version changed but nothing listed newer than lastSeen — don't spam;
        // just catch the stored version up.
        markVersionSeen();
      }
    }
    // Up-to-date returning user: do nothing.
  }, [user, isFirstRun, hasVersionBump, lastSeenVersion, tourCompleted, markVersionSeen]);

  const handleWhatsNewClose = useCallback(() => {
    setWhatsNewOpen(false);
    // Mark seen so it won't reappear until the next version bump.
    markVersionSeen();
  }, [markVersionSeen]);

  const openReleaseHistory = useCallback(() => {
    setReleaseHistoryOpen(true);
  }, []);

  const handleSeeFullHistory = useCallback(() => {
    // Opening full history from What's New also counts as "seen".
    setWhatsNewOpen(false);
    markVersionSeen();
    openReleaseHistory();
  }, [markVersionSeen, openReleaseHistory]);

  // FEAT-002: real tour start. Builds + runs the role-aware driver.js tour,
  // ignoring the completed flag (the auto-start effect below is the only place
  // that checks it). GuidedTour.startTour already wraps everything in try/catch.
  const startTour = useCallback(() => {
    startGuidedTour({
      role,
      isPhotographyOnly,
      isAdmin,
      onFinish: markTourCompleted,
    });
  }, [role, isPhotographyOnly, isAdmin, markTourCompleted]);

  // Keep the ref pointed at the latest startTour. Assigned during render (not in
  // an effect) so it is already current when the decision effect runs on the
  // first commit where the user becomes present.
  startTourRef.current = startTour;

  const contextValue = useMemo<OnboardingContextValue>(
    () => ({ openReleaseHistory, startTour }),
    [openReleaseHistory, startTour],
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
      <WhatsNewDialog
        open={whatsNewOpen}
        entries={whatsNewEntries}
        onClose={handleWhatsNewClose}
        onSeeFullHistory={handleSeeFullHistory}
      />
      <ReleaseHistoryDialog
        open={releaseHistoryOpen}
        onClose={() => setReleaseHistoryOpen(false)}
      />
    </OnboardingContext.Provider>
  );
};

export default OnboardingOrchestrator;
